import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  DialogContentText,
} from '@mui/material';
import DialogBasic from './DialogBasic';

/**
 * 처리 상세 내역 다이얼로그 컴포넌트
 * 
 * @param {boolean} open - 다이얼로그 열림 여부
 * @param {Function} onClose - 다이얼로그 닫기 핸들러
 * @param {Array} detailData - 상세 내역 데이터 배열
 * @param {string} title - 다이얼로그 제목 (기본값: "처리 상세 내역")
 * @param {string} closeButtonName - 닫기 버튼 텍스트 (기본값: "닫기")
 * @param {string} maxWidth - 다이얼로그 최대 너비 (기본값: "md")
 * @param {Object} columns - 테이블 컬럼 설정 (선택적)
 *   - 번호: { label: '번호', field: null }
 *   - 상태: { label: '상태', field: 'statusType' }
 *   - 메시지: { label: '메시지', field: 'message' }
 *   - 오류 메시지: { label: '오류 메시지', field: 'errorMsg' }
 */
function DetailResultDialog({
  open = false,
  onClose = () => { },
  detailData = [],
  title = '처리 상세 내역',
  closeButtonName = '닫기',
  maxWidth = 'md',
  columns = null, // 커스텀 컬럼 설정 (기본값 사용 시 null)
}) {
  // 기본 컬럼 설정
  const defaultColumns = [
    { label: '번호', field: null, width: '10%' },
    { label: '상태', field: 'statusType', width: '20%' },
    { label: '메시지', field: 'message', width: '35%' },
    { label: '오류 메시지', field: 'errorMsg', width: '35%' },
  ];

  const tableColumns = columns || defaultColumns;

  // 상태 색상 결정 함수
  const getStatusColor = (item) => {
    if (item.isSuccess) return '#2e7d32';
    if (item.statusType?.includes('ERROR')) return '#d32f2f';
    return '#1976d2';
  };

  // 상태 텍스트 결정 함수
  const getStatusText = (item) => {
    if (item.statusType) return item.statusType;
    return item.isSuccess ? '성공' : '실패';
  };

  // 필드 값 가져오기
  const getFieldValue = (item, field) => {
    if (field === null) return null; // 번호 필드는 index로 처리
    return item[field] || '-';
  };

  const renderContent = () => {
    if (!detailData || detailData.length === 0) {
      return (
        <DialogContentText>표시할 상세 내역이 없습니다.</DialogContentText>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Paper sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                {tableColumns.map((col, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      width: col.width,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {detailData.map((item, index) => (
                <TableRow key={index}>
                  {tableColumns.map((col, colIndex) => {
                    // 번호 컬럼 처리
                    if (col.field === null) {
                      return (
                        <TableCell key={colIndex}>{index + 1}</TableCell>
                      );
                    }

                    const value = getFieldValue(item, col.field);
                    const isErrorField = col.field === 'errorMsg';
                    const isStatusField = col.field === 'statusType';

                    return (
                      <TableCell key={colIndex}>
                        {isStatusField ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: getStatusColor(item),
                              fontWeight: 'bold',
                            }}
                          >
                            {getStatusText(item)}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: isErrorField && value !== '-' ? '#d32f2f' : 'inherit',
                            }}
                          >
                            {value}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    );
  };

  return (
    <DialogBasic
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      title={title}
      closeButtonName={closeButtonName}
      useCloseButton={true}
      useSaveButton={false}
      content={renderContent()}
    />
  );
}

export default React.memo(DetailResultDialog);

